import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Shield, UserPlus, Trash2, X, Copy, Check, MessageSquare, Phone, MessageCircle, Pencil, QrCode, CalendarOff, Plus, AtSign } from "lucide-react";
import { COLOR_PALETTE } from "@/lib/colors";

const EMPTY = { name: "", email: "", phone: "", password: "", role: "operative", color: "" };

function makeTempPassword() {
    // Human-friendly temp password: two words + 3 digits (e.g. Calm-Fern-482)
    const words = ["Calm", "Fern", "Oak", "Rain", "Ivy", "Sun", "Sky", "Fox", "Wren", "Sage"];
    const a = words[Math.floor(Math.random() * words.length)];
    let b = words[Math.floor(Math.random() * words.length)];
    while (b === a) b = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(100 + Math.random() * 900);
    return `${a}-${b}-${n}`;
}

// Normalise a phone number into E.164-style digits WhatsApp expects at wa.me/<digits>.
// Rules:
//   +353 87 123 4567 → 353871234567 (already international, strip +)
//   00353871234567   → 353871234567 (00-prefixed international dial)
//   087 123 4567     → 353871234567 (Irish trunk 0 → default country code)
//   353 87 123 4567  → 353871234567 (already has country code, no leading 0)
// Anything without recognisable digits returns "".
function toWaDigits(phone, defaultCc = "353") {
    if (!phone) return "";
    const raw = String(phone).trim();
    if (!raw) return "";
    if (raw.startsWith("+")) return raw.slice(1).replace(/\D+/g, "");
    if (raw.startsWith("00")) return raw.slice(2).replace(/\D+/g, "");
    const digits = raw.replace(/\D+/g, "");
    if (!digits) return "";
    if (digits.startsWith("0")) return defaultCc + digits.slice(1);
    return digits;
}

export default function TeamPage() {
    const { user } = useAuth();
    const isManager = user?.role === "manager";
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(null); // null | {}
    const [editing, setEditing] = useState(null); // null | {...user}
    const [justAdded, setJustAdded] = useState(null); // { name, email, password }
    const [qrFor, setQrFor] = useState(null); // { user, token, expires_at }
    const [holidays, setHolidays] = useState([]);
    const [holidayTarget, setHolidayTarget] = useState(null); // user for whom we're opening the time-off dialog

    const load = async () => {
        try {
            const [uRes, hRes] = await Promise.all([
                api.get("/users"),
                api.get("/holidays").catch(() => ({ data: [] })),
            ]);
            setUsers(uRes.data);
            setHolidays(hRes.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const removeMember = async (u) => {
        if (!window.confirm(`Remove ${u.name}? They will lose access immediately. Their assigned jobs will become unassigned.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            setUsers(prev => prev.filter(x => x.id !== u.id));
            toast.success(`${u.name} removed`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const managers = users.filter(u => u.role === "manager");
    const operatives = users.filter(u => u.role === "operative");

    const showQR = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.id}/onboarding-link`);
            setQrFor({ user: u, token: data.token, expires_at: data.expires_at });
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Team</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Your crew</h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm">Everyone with access to SCS.</p>
                </div>
                {isManager && (
                    <button
                        data-testid="add-member-btn"
                        onClick={() => setAdding({ ...EMPTY, password: makeTempPassword() })}
                        className="btn-primary inline-flex items-center gap-2 text-sm">
                        <UserPlus size={14} /> Add member
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading team…</div>
            ) : (
                <>
                    <Section title="Managers" count={managers.length}>
                        {managers.map(u => <MemberCard key={u.id} u={u} me={user} isManager={isManager} onDelete={removeMember} onEdit={setEditing} onShowQR={showQR} onShowHolidays={setHolidayTarget} holidays={holidays.filter(h => h.user_id === u.id)} />)}
                    </Section>
                    <Section title="Operatives" count={operatives.length}>
                        {operatives.length === 0 && (
                            <div className="col-span-full text-sm text-[var(--text-muted)] italic border border-dashed border-[var(--border-soft)] rounded-xl p-6 text-center">
                                No operatives yet. Add your first team member to start assigning jobs.
                            </div>
                        )}
                        {operatives.map(u => <MemberCard key={u.id} u={u} me={user} isManager={isManager} onDelete={removeMember} onEdit={setEditing} onShowQR={showQR} onShowHolidays={setHolidayTarget} holidays={holidays.filter(h => h.user_id === u.id)} />)}
                    </Section>
                </>
            )}

            {adding && (
                <AddMemberDialog
                    value={adding}
                    onChange={setAdding}
                    onClose={() => setAdding(null)}
                    onSaved={(newUser, pw) => {
                        setUsers(prev => [...prev, newUser].sort((a, b) => a.name.localeCompare(b.name)));
                        setAdding(null);
                        setJustAdded({ ...newUser, password: pw });
                    }}
                />
            )}

            {justAdded && (
                <CredentialsDialog
                    member={justAdded}
                    onClose={() => setJustAdded(null)}
                    onShowQR={async () => {
                        try {
                            const { data } = await api.post(`/users/${justAdded.id}/onboarding-link`);
                            setJustAdded(null);
                            setQrFor({ user: justAdded, token: data.token, expires_at: data.expires_at });
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                />
            )}

            {editing && (
                <EditMemberDialog
                    initial={editing}
                    isSelf={editing.id === user?.id}
                    onClose={() => setEditing(null)}
                    onSaved={(updated, newPassword) => {
                        setUsers(prev => prev.map(x => x.id === updated.id ? updated : x));
                        setEditing(null);
                        if (newPassword) {
                            setJustAdded({ ...updated, password: newPassword });
                        }
                    }}
                />
            )}

            {qrFor && (
                <OnboardQrDialog data={qrFor} onClose={() => setQrFor(null)} />
            )}

            {holidayTarget && (
                <HolidayDialog
                    member={holidayTarget}
                    holidays={holidays.filter(h => h.user_id === holidayTarget.id)}
                    onClose={() => setHolidayTarget(null)}
                    onChange={(next) => setHolidays(next)}
                />
            )}
        </div>
    );
}

function Section({ title, count, children }) {
    return (
        <section>
            <div className="flex items-center gap-2 mb-3">
                <h2 className="font-display text-xl font-semibold">{title}</h2>
                <span className="text-xs text-[var(--text-muted)]">({count})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
                {children}
            </div>
        </section>
    );
}

function MemberCard({ u, me, isManager, onDelete, onEdit, onShowQR, onShowHolidays, holidays }) {
    const isSelf = me?.id === u.id;
    const color = u.color || "";
    // Find any current or upcoming holidays (end_date >= today)
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = (holidays || []).filter(h => h.end_date >= today).sort((a, b) => a.start_date.localeCompare(b.start_date));
    const activeNow = upcoming.find(h => h.start_date <= today && h.end_date >= today);
    return (
        <div className="card-flat p-4 sm:p-5" data-testid={`team-member-${u.id}`}>
            <div className="flex items-start gap-3 sm:gap-4">
                <div
                    className="h-12 w-12 rounded-full flex items-center justify-center font-semibold shrink-0 border-2 text-sm"
                    style={{
                        background: color || "var(--pine-light)",
                        color: color ? "#fff" : "var(--pine)",
                        borderColor: color || "transparent",
                    }}
                >
                    {initialsOf(u.name)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold break-words leading-tight" data-testid={`member-name-${u.id}`}>{u.name}</div>
                    {u.email && !u.email.endsWith("@scs.local") ? (
                        <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5 mt-1 break-all">
                            <Mail size={12} className="shrink-0" /> {u.email}
                        </div>
                    ) : (
                        <div className="text-xs text-[var(--text-muted)] italic mt-1">Phone-only member</div>
                    )}
                    {u.phone && (
                        <a href={`tel:${u.phone.replace(/\s+/g, "")}`}
                           onClick={e => e.stopPropagation()}
                           className="text-xs text-[var(--text-secondary)] hover:text-[var(--pine)] flex items-center gap-1.5 mt-0.5">
                            <Phone size={12} className="shrink-0" /> {u.phone}
                        </a>
                    )}
                    {activeNow ? (
                        <div className="text-xs text-orange-700 font-medium flex items-center gap-1.5 mt-1.5">
                            <CalendarOff size={12} className="shrink-0" /> Off until {fmtShortDate(activeNow.end_date)}
                        </div>
                    ) : upcoming.length > 0 ? (
                        <div className="text-xs text-[var(--text-muted)] flex items-center gap-1.5 mt-1.5">
                            <CalendarOff size={12} className="shrink-0" /> Off {fmtShortDate(upcoming[0].start_date)} – {fmtShortDate(upcoming[0].end_date)}
                        </div>
                    ) : null}
                </div>
                <span className={`pill ${u.role === "manager" ? "status-completed" : "status-scheduled"} capitalize shrink-0`}>
                    <Shield size={12} /> {u.role}
                </span>
            </div>

            {isManager && (
                <div className="mt-3 pt-3 border-t border-[var(--border-soft)] flex items-center justify-end gap-1 flex-wrap">
                    <button
                        data-testid={`holidays-btn-${u.id}`}
                        onClick={() => onShowHolidays(u)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-[var(--text-secondary)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)] transition-colors"
                        title="Manage time off"
                        aria-label={`Time off for ${u.name}`}>
                        <CalendarOff size={13} /> Time off
                    </button>
                    {!isSelf && (
                        <button
                            data-testid={`qr-member-${u.id}`}
                            onClick={() => onShowQR(u)}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)] transition-colors"
                            title="Show scan-to-login QR"
                            aria-label={`Show QR for ${u.name}`}>
                            <QrCode size={14} />
                        </button>
                    )}
                    <button
                        data-testid={`edit-member-${u.id}`}
                        onClick={() => onEdit(u)}
                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)] transition-colors"
                        title="Edit member"
                        aria-label={`Edit ${u.name}`}>
                        <Pencil size={14} />
                    </button>
                    {!isSelf && (
                        <button
                            data-testid={`delete-member-${u.id}`}
                            onClick={() => onDelete(u)}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Remove member"
                            aria-label={`Remove ${u.name}`}>
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function fmtShortDate(iso) {
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
    } catch { return iso; }
}

function initialsOf(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ColorPicker({ value, onChange, testIdPrefix = "color" }) {
    return (
        <div className="mt-1 grid grid-cols-8 sm:grid-cols-10 gap-1.5">
            <button type="button"
                    data-testid={`${testIdPrefix}-none`}
                    onClick={() => onChange("")}
                    title="No colour"
                    className={`h-8 w-8 rounded-full border-2 transition-transform flex items-center justify-center bg-white ${
                        !value ? "border-black scale-110 shadow-md" : "border-[var(--border-soft)] hover:scale-105"
                    }`}>
                <X size={14} className="text-[var(--text-muted)]" />
            </button>
            {COLOR_PALETTE.map(c => (
                <button key={c} type="button"
                        data-testid={`${testIdPrefix}-${c}`}
                        onClick={() => onChange(c)}
                        title={c}
                        className={`h-8 w-8 rounded-full border-2 transition-transform ${
                            value === c ? "border-black scale-110 shadow-md" : "border-white hover:scale-105"
                        }`}
                        style={{ background: c }} />
            ))}
        </div>
    );
}

function AddMemberDialog({ value, onChange, onClose, onSaved }) {
    const [busy, setBusy] = useState(false);
    const set = (k, v) => onChange({ ...value, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        if (!value.name.trim() || value.password.length < 6) {
            toast.error("Fill in name and a password of at least 6 characters");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: value.name.trim(),
                phone: value.phone.trim(),
                password: value.password,
                role: value.role,
                color: value.color || "",
            };
            const emailTrimmed = value.email.trim().toLowerCase();
            if (emailTrimmed) payload.email = emailTrimmed;
            const { data } = await api.post("/users", payload);
            onSaved(data, value.password);
            toast.success(`${data.name} added`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="add-member-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-6 pb-4 flex items-start justify-between shrink-0 border-b border-[var(--border-soft)]">
                    <h2 className="font-display text-xl font-semibold">Add team member</h2>
                    <button type="button" onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-black"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                    <Field label="Full name">
                        <input data-testid="member-name-input" className="input-field mt-1"
                               value={value.name} onChange={e => set("name", e.target.value)}
                               placeholder="e.g. Sam O'Brien" autoFocus />
                    </Field>
                    <Field label="Email (optional)">
                        <input data-testid="member-email-input" type="email" className="input-field mt-1"
                               value={value.email} onChange={e => set("email", e.target.value)}
                               placeholder="Leave blank for phone-only members" />
                        <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                            Skip this and we'll generate a hidden login id — just share the password by SMS.
                        </p>
                    </Field>
                    <Field label="Mobile (optional)">
                        <input data-testid="member-phone-input" type="tel" className="input-field mt-1"
                               value={value.phone} onChange={e => set("phone", e.target.value)}
                               placeholder="+353 87 123 4567" />
                        <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                            Add their number and you'll be able to send the login by SMS in one tap.
                        </p>
                    </Field>
                    <Field label="Role">
                        <div className="mt-1 grid grid-cols-2 gap-2">
                            {["operative", "manager"].map(r => (
                                <button key={r} type="button"
                                        data-testid={`member-role-${r}`}
                                        onClick={() => set("role", r)}
                                        className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors capitalize ${
                                            value.role === r
                                                ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                                : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                        }`}>
                                    {r}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Colour tag">
                        <p className="text-[11px] text-[var(--text-muted)] mt-1 mb-1">
                            Shows on their avatar and on the schedule — makes it easy to spot who's on which job.
                        </p>
                        <ColorPicker value={value.color || ""} onChange={(c) => set("color", c)} testIdPrefix="member-color" />
                    </Field>
                    <Field label="Temporary password">
                        <div className="mt-1 flex gap-2">
                            <input data-testid="member-password-input" className="input-field flex-1"
                                   value={value.password}
                                   onChange={e => set("password", e.target.value)}
                                   placeholder="At least 6 characters" />
                            <button type="button"
                                    data-testid="regenerate-password-btn"
                                    onClick={() => set("password", makeTempPassword())}
                                    className="btn-ghost text-xs whitespace-nowrap">
                                Suggest
                            </button>
                        </div>
                        <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                            Share this with them by text or WhatsApp. They can change it once they log in.
                        </p>
                    </Field>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button data-testid="save-member-btn" type="submit" className="btn-primary" disabled={busy}>
                            {busy ? "Adding…" : "Add member"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CredentialsDialog({ member, onClose, onShowQR }) {
    const [copied, setCopied] = useState(false);
    const isSynthetic = (member.email || "").endsWith("@scs.local");
    const loginLabel = isSynthetic ? "Login id" : "Email";
    const loginUrl = typeof window !== "undefined" ? `${window.location.origin}/login` : "/login";
    const smsBody = `Hi ${member.name.split(" ")[0]}, here's your SCS login:\n${loginLabel}: ${member.email}\nPassword: ${member.password}\n\nSign in here: ${loginUrl}\n(You can change your password once you're in.)`;
    const text = `SCS login for ${member.name}\n${loginLabel}: ${member.email}\nPassword: ${member.password}\nSign in: ${loginUrl}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy — please copy manually"); }
    };
    const smsHref = member.phone
        ? `sms:${member.phone.replace(/\s+/g, "")}?&body=${encodeURIComponent(smsBody)}`
        : null;
    // WhatsApp requires digits only in E.164 (no +, spaces, dashes). https://wa.me/<digits>
    // Uses toWaDigits so Irish local numbers like "087 123 4567" become "353871234567".
    const waDigits = toWaDigits(member.phone);
    const waHref = waDigits
        ? `https://wa.me/${waDigits}?text=${encodeURIComponent(smsBody)}`
        : null;
    // Email deep-link uses the mailto: scheme — opens Gmail / Apple Mail /
    // Outlook prefilled. We deliberately skip a `to` address if there's no
    // real email on file (synthetic @scs.local ids), so the manager types
    // whichever address they'd like the invite to land on.
    const realEmail = member.email && !isSynthetic ? member.email : "";
    const emailSubject = `Your SCS sign-in — ${member.name}`;
    const emailBody = `Hi ${member.name.split(" ")[0]},\n\nHere are your SCS sign-in details:\n\n${loginLabel}: ${member.email}\nPassword: ${member.password}\n\nSign in on any device (phone, tablet, laptop) at:\n${loginUrl}\n\nOnce you're in, tap the "Install" button in the top-right of the header to add SCS to your home screen or desktop.\n\nYou can change your password once you're signed in.`;
    const emailHref = `mailto:${encodeURIComponent(realEmail)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="credentials-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
                <div className="p-6">
                    <h2 className="font-display text-xl font-semibold">Share these details with {member.name.split(" ")[0]}</h2>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                        You won't see this password again — send it over now while it's on screen.
                    </p>
                    <div className="mt-4 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] p-4 space-y-2 font-mono text-sm">
                        <div><span className="text-[var(--text-muted)]">{loginLabel}:</span> <b>{member.email}</b></div>
                        <div><span className="text-[var(--text-muted)]">Password:</span> <b data-testid="new-member-password">{member.password}</b></div>
                        {member.phone && (
                            <div><span className="text-[var(--text-muted)]">Mobile:</span> <b>{member.phone}</b></div>
                        )}
                        <div className="pt-1 mt-1 border-t border-[var(--border-soft)] break-all">
                            <span className="text-[var(--text-muted)]">Sign in at:</span> <b data-testid="login-url">{loginUrl}</b>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 mt-5">
                        <button className="btn-ghost" onClick={onClose}>Done</button>
                        {onShowQR && (
                            <button
                                data-testid="show-qr-btn"
                                onClick={onShowQR}
                                type="button"
                                className="btn-ghost inline-flex items-center gap-2">
                                <QrCode size={14} /> Show QR code
                            </button>
                        )}
                        <button data-testid="copy-credentials-btn" onClick={copy} className="btn-ghost inline-flex items-center gap-2">
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy login"}
                        </button>
                        {waHref ? (
                            <a data-testid="send-whatsapp-btn" href={waHref} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE57] text-white px-4 py-2 text-sm font-semibold transition-colors">
                                <MessageCircle size={14} /> WhatsApp
                            </a>
                        ) : (
                            <button
                                data-testid="send-whatsapp-btn-disabled"
                                type="button"
                                disabled
                                title="Add a mobile number to use WhatsApp"
                                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold opacity-50 cursor-not-allowed">
                                <MessageCircle size={14} /> WhatsApp
                            </button>
                        )}
                        <a
                            data-testid="send-email-btn"
                            href={emailHref}
                            className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:opacity-90 text-white px-4 py-2 text-sm font-semibold transition-opacity"
                            title={realEmail ? `Email ${realEmail}` : "Open a blank email — type the address"}>
                            <AtSign size={14} /> Email
                        </a>
                        {smsHref ? (
                            <a data-testid="send-sms-btn" href={smsHref}
                               className="btn-primary inline-flex items-center gap-2">
                                <MessageSquare size={14} /> Send by SMS
                            </a>
                        ) : (
                            <button
                                data-testid="send-sms-btn-disabled"
                                type="button"
                                disabled
                                title="Add a mobile number to use SMS"
                                className="btn-primary inline-flex items-center gap-2 opacity-50 cursor-not-allowed">
                                <MessageSquare size={14} /> Send by SMS
                            </button>
                        )}
                    </div>
                    {!member.phone && (
                        <p className="text-[11px] text-[var(--text-muted)] mt-3 flex items-center gap-1">
                            <Phone size={11} /> No mobile on file — add one next time to send the login by SMS or WhatsApp in one tap.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--text-muted)]">{label}</label>
            {children}
        </div>
    );
}

function EditMemberDialog({ initial, isSelf, onClose, onSaved }) {
    const [value, setValue] = useState({
        name: initial.name || "",
        phone: initial.phone || "",
        role: initial.role || "operative",
        color: initial.color || "",
    });
    const [resetPw, setResetPw] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setValue(prev => ({ ...prev, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!value.name.trim()) {
            toast.error("Name can't be empty");
            return;
        }
        if (resetPw && newPassword.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        setBusy(true);
        try {
            const body = {
                name: value.name.trim(),
                phone: value.phone.trim(),
                role: value.role,
                color: value.color || "",
            };
            if (resetPw) body.password = newPassword;
            const { data } = await api.patch(`/users/${initial.id}`, body);
            toast.success(`${data.name} updated`);
            onSaved(data, resetPw ? newPassword : null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="edit-member-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-6 pb-4 flex items-start justify-between shrink-0 border-b border-[var(--border-soft)]">
                    <h2 className="font-display text-xl font-semibold">Edit {initial.name.split(" ")[0]}</h2>
                    <button type="button" onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-black"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                    <Field label="Full name">
                        <input data-testid="edit-name-input" className="input-field mt-1"
                               value={value.name} onChange={e => set("name", e.target.value)}
                               autoFocus />
                    </Field>
                    <Field label="Email">
                        <input className="input-field mt-1 bg-[var(--surface-soft)] cursor-not-allowed"
                               value={initial.email} disabled
                               data-testid="edit-email-input" />
                        <p className="text-[11px] text-[var(--text-muted)] mt-1.5">Email can't be changed — remove and re-add if it needs to change.</p>
                    </Field>
                    <Field label="Mobile">
                        <input data-testid="edit-phone-input" type="tel" className="input-field mt-1"
                               value={value.phone} onChange={e => set("phone", e.target.value)}
                               placeholder="+353 87 123 4567" />
                    </Field>
                    <Field label="Role">
                        <div className="mt-1 grid grid-cols-2 gap-2">
                            {["operative", "manager"].map(r => (
                                <button key={r} type="button"
                                        data-testid={`edit-role-${r}`}
                                        disabled={isSelf}
                                        onClick={() => !isSelf && set("role", r)}
                                        className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors capitalize ${
                                            value.role === r
                                                ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                                : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                        } ${isSelf ? "opacity-60 cursor-not-allowed" : ""}`}>
                                    {r}
                                </button>
                            ))}
                        </div>
                        {isSelf && <p className="text-[11px] text-[var(--text-muted)] mt-1.5">You can't change your own role.</p>}
                    </Field>

                    <Field label="Colour tag">
                        <p className="text-[11px] text-[var(--text-muted)] mt-1 mb-1">
                            Shows on their avatar and on the schedule.
                        </p>
                        <ColorPicker value={value.color || ""} onChange={(c) => set("color", c)} testIdPrefix="edit-color" />
                    </Field>

                    {!resetPw ? (
                        <button type="button"
                                data-testid="reset-password-toggle"
                                onClick={() => { setResetPw(true); setNewPassword(makeTempPassword()); }}
                                className="btn-ghost inline-flex items-center gap-2 text-sm">
                            Reset password…
                        </button>
                    ) : (
                        <Field label="New temporary password">
                            <div className="mt-1 flex gap-2">
                                <input data-testid="reset-password-input" className="input-field flex-1"
                                       value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                       placeholder="At least 6 characters" autoFocus />
                                <button type="button"
                                        data-testid="reset-suggest-btn"
                                        onClick={() => setNewPassword(makeTempPassword())}
                                        className="btn-ghost text-xs whitespace-nowrap">
                                    Suggest
                                </button>
                                <button type="button"
                                        data-testid="reset-cancel-btn"
                                        onClick={() => { setResetPw(false); setNewPassword(""); }}
                                        className="btn-ghost text-xs whitespace-nowrap">
                                    Cancel
                                </button>
                            </div>
                            <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                                After saving, we'll show a share dialog so you can send the new login by SMS or WhatsApp.
                            </p>
                        </Field>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button data-testid="save-edit-btn" type="submit" className="btn-primary" disabled={busy}>
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


function OnboardQrDialog({ data, onClose }) {
    // Build a URL the phone camera can open. Route "/onboard/:token" auto-signs in.
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const link = `${origin}/onboard/${data.token}`;
    const expiresLabel = (() => {
        try {
            return new Date(data.expires_at).toLocaleDateString(undefined, {
                weekday: "long", day: "numeric", month: "short"
            });
        } catch { return null; }
    })();
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy — please copy manually"); }
    };
    const smsBody = `Hi ${data.user.name.split(" ")[0]}, tap this link to sign into SCS — works on any device (phone, tablet, laptop). No id or password needed:\n${link}\n(This link expires ${expiresLabel || "in 60 days"} and can only be used once — for one device. Ask your manager for a fresh link for a second device.)`;
    const waDigits = toWaDigits(data.user.phone);
    const waHref = waDigits ? `https://wa.me/${waDigits}?text=${encodeURIComponent(smsBody)}` : null;
    const smsHref = data.user.phone ? `sms:${data.user.phone.replace(/\s+/g, "")}?&body=${encodeURIComponent(smsBody)}` : null;
    // Email: works even for phone-only members (opens a blank draft so the
    // manager can type any address). For members with a real email we
    // pre-fill the "to" field.
    const isSyntheticEmail = (data.user.email || "").endsWith("@scs.local");
    const realEmail = data.user.email && !isSyntheticEmail ? data.user.email : "";
    const emailSubject = `Your SCS sign-in link`;
    const emailBody = `Hi ${data.user.name.split(" ")[0]},\n\nClick the link below on the device you want to sign in on (phone, tablet or laptop) — it will sign you in instantly. No email or password needed.\n\n${link}\n\nOnce you're in, tap the "Install" button (⬇ icon) in the top-right of the header to add SCS to your home screen or desktop.\n\nNote: this link works on ONE device only and expires ${expiresLabel || "in 60 days"}. If you need it on a second device, just ask your manager to send a fresh one.`;
    const emailHref = `mailto:${encodeURIComponent(realEmail)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="qr-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
                <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="font-display text-xl font-semibold">Sign-in link for {data.user.name.split(" ")[0]}</h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">
                                They can scan this QR from a <b>phone camera</b>, or you can send the link by <b>WhatsApp</b>, <b>Email</b> or <b>SMS</b>. Works on any device — phone, tablet, or laptop.
                            </p>
                        </div>
                        <button className="p-2 rounded-full hover:bg-[var(--bg-subtle)] transition-colors -mr-2 -mt-2"
                                onClick={onClose} aria-label="Close" data-testid="close-qr-btn">
                            <X size={18} />
                        </button>
                    </div>
                    <div className="flex items-center justify-center mt-5 mb-4">
                        <div className="p-4 bg-white border-2 border-[var(--border-soft)] rounded-2xl">
                            <QRCodeSVG value={link} size={220} level="M" includeMargin={false} />
                        </div>
                    </div>
                    <div className="text-xs text-[var(--text-muted)] text-center">
                        Single-use · one device per link{expiresLabel ? ` · expires ${expiresLabel}` : ""}
                    </div>
                    <p className="text-[11px] text-[var(--text-muted)] text-center mt-2 px-2">
                        Sending to a laptop or tablet? Send this same link by <b>Email</b> — they'll click it on that device and be signed in.
                        For a second device, just close this and tap the QR icon again for a fresh link.
                    </p>
                    <div className="flex flex-wrap justify-end gap-2 mt-5">
                        <button className="btn-ghost" onClick={onClose}>Done</button>
                        <button data-testid="copy-qr-link-btn" onClick={copy} className="btn-ghost inline-flex items-center gap-2">
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy link"}
                        </button>
                        {waHref && (
                            <a data-testid="qr-whatsapp-btn" href={waHref} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE57] text-white px-4 py-2 text-sm font-semibold transition-colors">
                                <MessageCircle size={14} /> WhatsApp
                            </a>
                        )}
                        <a
                            data-testid="qr-email-btn"
                            href={emailHref}
                            className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:opacity-90 text-white px-4 py-2 text-sm font-semibold transition-opacity"
                            title={realEmail ? `Email ${realEmail}` : "Open a blank email — type the address"}>
                            <AtSign size={14} /> Email
                        </a>
                        {smsHref && (
                            <a data-testid="qr-sms-btn" href={smsHref}
                               className="btn-primary inline-flex items-center gap-2">
                                <MessageSquare size={14} /> SMS
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function HolidayDialog({ member, holidays, onClose, onChange }) {
    const today = new Date().toISOString().slice(0, 10);
    const [start, setStart] = useState(today);
    const [end, setEnd] = useState(today);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!start || !end) { toast.error("Pick a start and end date"); return; }
        if (end < start) { toast.error("End date must be on or after start"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/holidays", {
                user_id: member.id,
                start_date: start,
                end_date: end,
                reason: reason.trim(),
            });
            // Refetch all holidays so schedule stays in sync
            const { data: fresh } = await api.get("/holidays");
            onChange(fresh);
            setReason("");
            toast.success(`${member.name.split(" ")[0]}: time off added`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Remove this time off?")) return;
        try {
            await api.delete(`/holidays/${id}`);
            const { data: fresh } = await api.get("/holidays");
            onChange(fresh);
            toast.success("Time off removed");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const sorted = [...holidays].sort((a, b) => a.start_date.localeCompare(b.start_date));

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="holiday-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-6 flex items-start justify-between">
                    <div>
                        <h2 className="font-display text-xl font-semibold">Time off for {member.name.split(" ")[0]}</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">Add days when they're not working — they'll show as grey bars on the schedule.</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-black" data-testid="close-holiday-btn"><X size={18} /></button>
                </div>

                <div className="px-6 pb-6 space-y-4 overflow-y-auto">
                    <form onSubmit={submit} className="space-y-3 p-4 rounded-xl bg-[var(--surface-soft)]">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">From</label>
                                <input type="date" data-testid="holiday-start-input" className="input-field mt-1"
                                       value={start} onChange={e => setStart(e.target.value)} required />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">To</label>
                                <input type="date" data-testid="holiday-end-input" className="input-field mt-1"
                                       value={end} onChange={e => setEnd(e.target.value)} min={start || undefined} required />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">Reason (optional)</label>
                            <input data-testid="holiday-reason-input" className="input-field mt-1"
                                   value={reason} onChange={e => setReason(e.target.value)}
                                   placeholder="Family, sick, annual leave…" />
                        </div>
                        <div className="flex justify-end">
                            <button type="submit" data-testid="add-holiday-btn" className="btn-primary inline-flex items-center gap-2" disabled={busy}>
                                <Plus size={14} /> {busy ? "Adding…" : "Add time off"}
                            </button>
                        </div>
                    </form>

                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold mb-2">
                            Scheduled ({sorted.length})
                        </div>
                        {sorted.length === 0 ? (
                            <div className="text-sm text-[var(--text-muted)] italic py-4 text-center border border-dashed border-[var(--border-soft)] rounded-xl">
                                Nothing scheduled yet.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="holidays-list">
                                {sorted.map(h => (
                                    <li key={h.id} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-soft)]">
                                        <CalendarOff size={14} className="text-[var(--text-muted)] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium">
                                                {fmtShortDate(h.start_date)} – {fmtShortDate(h.end_date)}
                                            </div>
                                            {h.reason && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{h.reason}</div>}
                                        </div>
                                        <button
                                            data-testid={`delete-holiday-${h.id}`}
                                            onClick={() => remove(h.id)}
                                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-red-600 hover:bg-red-50 transition-colors"
                                            aria-label="Remove this time off">
                                            <Trash2 size={14} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

